import apiClient from './client';
import { API_PATHS } from './paths';
import type { ApiResponse } from '../types/api';
import type { LanguageCode } from '../types/i18n';
import type { WalkInSlotsResponse, CreateWalkInBookingReq, WalkInBookingResponse } from '../types/providerWalkInBooking';
import type {
  ProviderProfile,
  ProviderStats,
  ProviderService,
  ServiceType,
  Booking,
  BookingPage,
  BookingStatus,
  TodayStatus,
  TodayBooking,
  WeeklySchedule,
  WorkingHours,
  ScheduleBreak,
  ScheduleException,
  ProviderSchedule,
  ScheduleDayEntry,
  ScheduleOffDate,
  DayOfWeek,
  AnalyticsData,
  AnalyticsPeriod,
  BookingDeclineReason,
} from '../types/provider';

// ─── Provider Profile & Stats ───────────────────────────────────────────────

export const providerMeApi = {
  getProfile: async (): Promise<ProviderProfile> => {
    const res = await apiClient.get<ApiResponse<ProviderProfile>>('/v1/provider/me');
    return res.data.data;
  },

  updateProfile: async (data: {
    displayName?: string;
    bio?: string | null;
    defaultBookingMode?: string;
    defaultDepositAmount?: number | null;
    translations?: Array<{ languageCode: string; displayName: string; bio: string | null }>;
  }): Promise<ProviderProfile> => {
    const res = await apiClient.put<ApiResponse<ProviderProfile>>('/v1/provider/me', data);
    return res.data.data;
  },

  uploadPhoto: async (file: File): Promise<ProviderProfile> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await apiClient.post<ApiResponse<ProviderProfile>>(
      '/v1/provider/me/photo',
      formData,
    );
    return res.data.data;
  },

  deletePhoto: async (): Promise<ProviderProfile> => {
    const res = await apiClient.delete<ApiResponse<ProviderProfile>>('/v1/provider/me/photo');
    return res.data.data;
  },

  setActive: async (active: boolean): Promise<ProviderProfile> => {
    const endpoint = active ? '/v1/provider/me/activate' : '/v1/provider/me/deactivate';
    const res = await apiClient.put<ApiResponse<ProviderProfile>>(endpoint);
    return res.data.data;
  },

  getStats: async (): Promise<ProviderStats> => {
    const res = await apiClient.get<ApiResponse<ProviderStats>>('/v1/provider/me/stats');
    return res.data.data;
  },

  getAnalytics: async (period: AnalyticsPeriod): Promise<AnalyticsData> => {
    const res = await apiClient.get<ApiResponse<AnalyticsData>>(
      '/v1/provider/me/analytics',
      { params: { period } },
    );
    return res.data.data;
  },

  getTodayStatus: async (): Promise<TodayStatus> => {
    const res = await apiClient.get<ApiResponse<TodayStatus>>('/v1/provider/me/today-status');
    return res.data.data;
  },

  getNextBooking: async (): Promise<TodayBooking | null> => {
    const res = await apiClient.get<ApiResponse<TodayBooking | null>>('/v1/provider/me/next-booking');
    return res.data.data;
  },

  getTodayBookings: async (): Promise<TodayBooking[]> => {
    const res = await apiClient.get<ApiResponse<TodayBooking[]>>('/v1/provider/me/bookings');
    return res.data.data;
  },

  getServiceTypes: async (activeOnly = true): Promise<ServiceType[]> => {
    const res = await apiClient.get<ApiResponse<ServiceType[]>>('/v1/service-types', {
      params: { activeOnly },
    });
    return res.data.data;
  },

  getServices: async (): Promise<ProviderService[]> => {
    const res = await apiClient.get<ApiResponse<ProviderService[]>>('/v1/provider/me/services');
    return res.data.data;
  },

  getSlots: async (serviceId: number, date: string): Promise<WalkInSlotsResponse> => {
    const res = await apiClient.get<ApiResponse<WalkInSlotsResponse>>(
      '/v1/provider/me/slots',
      { params: { serviceId, date } },
    );
    return res.data.data;
  },

  createService: async (data: {
    serviceTypeId: number;
    durationMinutes: number;
    price: number;
    bookingMode?: string;
  }): Promise<ProviderService> => {
    const res = await apiClient.post<ApiResponse<ProviderService>>('/v1/provider/me/services', data);
    return res.data.data;
  },

  updateService: async (serviceId: number, data: {
    durationMinutes?: number;
    price?: number;
    bookingMode?: string;
  }): Promise<ProviderService> => {
    const res = await apiClient.put<ApiResponse<ProviderService>>(`/v1/provider/me/services/${serviceId}`, data);
    return res.data.data;
  },

  activateService: async (serviceId: number): Promise<ProviderService> => {
    const res = await apiClient.put<ApiResponse<ProviderService>>(`/v1/provider/me/services/${serviceId}/activate`);
    return res.data.data;
  },

  deactivateService: async (serviceId: number): Promise<ProviderService> => {
    const res = await apiClient.put<ApiResponse<ProviderService>>(`/v1/provider/me/services/${serviceId}/deactivate`);
    return res.data.data;
  },

  updateLanguage: async (
    languageCode: LanguageCode,
  ): Promise<{ preferredLanguage: LanguageCode }> => {
    const res = await apiClient.patch<ApiResponse<{ preferredLanguage: LanguageCode }>>(
      API_PATHS.PROVIDER_LANGUAGE_UPDATE,
      { languageCode },
    );
    return res.data.data;
  },
};

// ─── Bookings ───────────────────────────────────────────────────────────────

export const bookingApi = {
  getProviderBookings: async (page = 0, size = 20): Promise<BookingPage> => {
    const res = await apiClient.get<ApiResponse<BookingPage>>('/v1/bookings/provider', {
      params: { page, size },
    });
    return res.data.data;
  },

  search: async (params: {
    status?: BookingStatus | BookingStatus[];
    startDate?: string;
    endDate?: string;
    page?: number;
    size?: number;
  }): Promise<BookingPage> => {
    const res = await apiClient.get<ApiResponse<BookingPage>>('/v1/bookings/search', { params });
    return res.data.data;
  },

  complete: async (bookingId: number): Promise<void> => {
    await apiClient.post(`/v1/provider/bookings/${bookingId}/complete`);
  },

  getByDate: async (date: string): Promise<BookingPage> => {
    const res = await apiClient.get<ApiResponse<BookingPage>>('/v1/bookings/search', {
      params: { startDate: date, endDate: date, size: 50 },
    });
    return res.data.data;
  },

  getById: async (bookingId: number): Promise<Booking> => {
    const res = await apiClient.get<ApiResponse<Booking>>(`/v1/bookings/${bookingId}`);
    return res.data.data;
  },

  confirm: async (bookingId: number): Promise<void> => {
    await apiClient.post(`/v1/provider/bookings/${bookingId}/confirm`);
  },

  decline: async (bookingId: number, reason: BookingDeclineReason): Promise<void> => {
    await apiClient.post(`/v1/provider/bookings/${bookingId}/decline`, { reason });
  },

  cancelByProvider: async (bookingId: number): Promise<void> => {
    await apiClient.post(`/v1/provider/bookings/${bookingId}/cancel`);
  },

  createWalkIn: async (
    req: CreateWalkInBookingReq,
    idempotencyKey: string,
  ): Promise<WalkInBookingResponse> => {
    const res = await apiClient.post<ApiResponse<WalkInBookingResponse>>(
      '/v1/provider/bookings/walk-in',
      req,
      { headers: { 'Idempotency-Key': idempotencyKey } },
    );
    return res.data.data;
  },

  markAsPaid: async (bookingId: number): Promise<Booking> => {
    const res = await apiClient.patch<ApiResponse<Booking>>(
      `/v1/provider/bookings/${bookingId}/payment-status`,
      { paymentStatus: 'PAID' },
    );
    return res.data.data;
  },

  markNoShow: async (bookingId: number, reason: string): Promise<void> => {
    await apiClient.post(
      `/v1/provider/bookings/${bookingId}/no-show`,
      { reason },
    );
  },
};

// ─── New Schedule API ────────────────────────────────────────────────────────

export const providerScheduleApi = {
  getSchedule: async (): Promise<ProviderSchedule> => {
    const res = await apiClient.get<ApiResponse<ProviderSchedule>>('/v1/provider/me/schedule');
    return res.data.data;
  },

  updateWorkingHours: async (id: number, data: {
    startTime?: string;
    endTime?: string;
    enabled?: boolean;
  }): Promise<ScheduleDayEntry> => {
    const res = await apiClient.put<ApiResponse<ScheduleDayEntry>>(`/v1/provider/me/schedule/working-hours/${id}`, data);
    return res.data.data;
  },

  upsertWorkingHours: async (data: {
    dayOfWeek: DayOfWeek;
    startTime: string;
    endTime: string;
  }): Promise<ScheduleDayEntry> => {
    const DAY_TO_INT: Record<DayOfWeek, number> = {
      MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4,
      FRIDAY: 5, SATURDAY: 6, SUNDAY: 7,
    };
    const res = await apiClient.put<ApiResponse<ScheduleDayEntry>>(
      '/v1/provider/me/schedule/working-hours/upsert',
      { dayOfWeek: DAY_TO_INT[data.dayOfWeek], startTime: data.startTime, endTime: data.endTime },
    );
    return res.data.data;
  },

  addOffDate: async (data: {
    date: string;
    reason?: string;
  }): Promise<ScheduleOffDate> => {
    const res = await apiClient.post<ApiResponse<ScheduleOffDate>>('/v1/provider/me/schedule/off-dates', data);
    return res.data.data;
  },

  deleteOffDate: async (id: number): Promise<void> => {
    await apiClient.delete(`/v1/provider/me/schedule/off-dates/${id}`);
  },
};

// ─── Schedule (legacy) ───────────────────────────────────────────────────────

export const scheduleApi = {
  getWeekly: async (providerId: number): Promise<WeeklySchedule> => {
    const res = await apiClient.get<ApiResponse<WeeklySchedule>>(`/schedule/working-hours/${providerId}/weekly`);
    return res.data.data;
  },

  getWorkingHours: async (providerId: number): Promise<WorkingHours[]> => {
    const res = await apiClient.get<ApiResponse<WorkingHours[]>>(`/schedule/working-hours/${providerId}`);
    return res.data.data;
  },

  setWorkingHours: async (data: {
    serviceProviderId: number;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  }): Promise<WorkingHours> => {
    const res = await apiClient.post<ApiResponse<WorkingHours>>('/schedule/working-hours', data);
    return res.data.data;
  },

  deleteWorkingHours: async (id: number): Promise<void> => {
    await apiClient.delete(`/schedule/working-hours/${id}`);
  },

  getBreaks: async (providerId: number): Promise<ScheduleBreak[]> => {
    const res = await apiClient.get<ApiResponse<ScheduleBreak[]>>(`/schedule/breaks/${providerId}`);
    return res.data.data;
  },

  createBreak: async (data: {
    serviceProviderId: number;
    dayOfWeek: number;
    startTime: string;
    endTime: string;
  }): Promise<ScheduleBreak> => {
    const res = await apiClient.post<ApiResponse<ScheduleBreak>>('/schedule/breaks', data);
    return res.data.data;
  },

  deleteBreak: async (id: number): Promise<void> => {
    await apiClient.delete(`/schedule/breaks/${id}`);
  },

  getExceptions: async (providerId: number): Promise<ScheduleException[]> => {
    const res = await apiClient.get<ApiResponse<ScheduleException[]>>(`/schedule/exceptions/${providerId}`);
    return res.data.data;
  },

  createException: async (data: {
    serviceProviderId: number;
    exceptionDate: string;
    startTime?: string;
    endTime?: string;
    reason?: string;
  }): Promise<ScheduleException> => {
    const res = await apiClient.post<ApiResponse<ScheduleException>>('/schedule/exceptions', data);
    return res.data.data;
  },

  deleteException: async (id: number): Promise<void> => {
    await apiClient.delete(`/schedule/exceptions/${id}`);
  },
};

// ─── Notifications ──────────────────────────────────────────────────────────

export interface NotificationPreferences {
  enableTelegram: boolean;
  enableReminders: boolean;
  enableBookingUpdates: boolean;
}

export const notificationApi = {
  getPreferences: async (): Promise<NotificationPreferences> => {
    const res = await apiClient.get<ApiResponse<NotificationPreferences>>('/v1/notifications/preferences');
    return res.data.data;
  },

  updatePreferences: async (data: Partial<NotificationPreferences>): Promise<NotificationPreferences> => {
    const res = await apiClient.put<ApiResponse<NotificationPreferences>>('/v1/notifications/preferences', data);
    return res.data.data;
  },
};

// ─── Calendar (PREMIUM) ────────────────────────────────────────────────────

/**
 * Calendar feature mavjud emaslik sababi (backend qaytaradi).
 *
 * - `FEATURE_DISABLED` — platform admin kill switch'ni o'chirib qo'ygan.
 *   UI'da bu sabab foydalanuvchiga ko'rsatilmaydi (card umuman renderlanmaydi).
 * - `PREMIUM_REQUIRED` — tenant tarifi PREMIUM emas.
 */
export type CalendarUnavailableReason = 'FEATURE_DISABLED' | 'PREMIUM_REQUIRED';

export interface CalendarStatus {
  /** Provayder Google hisobini ulaganmi. */
  connected: boolean;
  /**
   * Calendar funksiyasi tenant uchun hozir mavjudmi. `false` bo'lsa `reason`
   * sababini ko'rsatadi (platform kill switch yoki PREMIUM emas).
   */
  available: boolean;
  /** Mavjud emaslik sababi (`available === false` bo'lganda to'ldiriladi). */
  reason: CalendarUnavailableReason | null;
}

/**
 * Backend outbox tomonidan FAILED holatga o'tgan sync yozuvi.
 * OWNER monitoring paneli ushbu ro'yxatni ko'rsatadi va `retryFailedSync`
 * orqali qayta urinishni rejalashtirish mumkin.
 */
export interface FailedSync {
  syncId: number;
  bookingId: number;
  serviceProviderId: number;
  syncStatus: 'FAILED';
  lastSyncError: string;
  retryCount: number;
  lastSyncAt: string;
  createdAt: string;
}

export const calendarApi = {
  getAuthUrl: async (tenantId: number, providerId: number): Promise<string> => {
    const res = await apiClient.get<ApiResponse<{ authUrl: string }>>(
      `/v1/tenants/${tenantId}/service-providers/${providerId}/calendar/auth/connect`
    );
    return res.data.data.authUrl;
  },

  getStatus: async (tenantId: number, providerId: number): Promise<CalendarStatus> => {
    const res = await apiClient.get<ApiResponse<CalendarStatus>>(
      `/v1/tenants/${tenantId}/service-providers/${providerId}/calendar/status`
    );
    return res.data.data;
  },

  disconnect: async (tenantId: number, providerId: number): Promise<void> => {
    await apiClient.delete(
      `/v1/tenants/${tenantId}/service-providers/${providerId}/calendar/disconnect`
    );
  },

  /**
   * OWNER monitoring: FAILED sync yozuvlari ro'yxati.
   * Faqat OWNER roli uchun; SERVICE_PROVIDER chaqirsa backend 403 qaytaradi.
   */
  getFailedSyncs: async (tenantId: number): Promise<FailedSync[]> => {
    const res = await apiClient.get<ApiResponse<FailedSync[]>>(
      `/v1/tenants/${tenantId}/calendar/sync/failed`,
    );
    return res.data.data;
  },

  /**
   * Bitta FAILED sync yozuvini qayta urinishga rejalashtirish.
   * Backend 202 Accepted qaytaradi; haqiqiy sinxronizatsiya 30 soniya ichida
   * outbox processor tomonidan amalga oshiriladi.
   */
  retryFailedSync: async (
    tenantId: number,
    bookingId: number,
  ): Promise<void> => {
    await apiClient.post(
      `/v1/tenants/${tenantId}/calendar/sync/${bookingId}/retry`,
    );
  },
};
